
import {
  computed,
  defineComponent, onMounted, reactive, ref, watch, Ref,
} from 'vue';
import AppSkeletonCase from '@/components/skeletons/AppSkeletonCase.vue';
import { useDriveStore } from '@/modules/drive/store/driveStore';
import { useMainStore } from '@/store/mainStore';
import AppCaseViewPopUp from '@/components/AppCaseViewPopUp.vue';
import AppMessageShareSettings from '@/components/AppMessageShareSettings.vue';
import ItemCase from '@/modules/drive/components/items/ItemCase.vue';
import { PHOTO_VIEW_TYPE, SHARE_SETTINGS } from '@/types/enums';
import WebsocketService from '@/services/WebsocketService';
import { useMessagesStore } from '@/modules/messages/store/messagesStore';
import UsePagination from '@/composables/UsePagination';
import { ICase } from '@/types/interfaces';
import DriveService from '@/modules/drive/services/DriveService';
import { DIRECTION } from '@/helpers/isScrollEnd';

export default defineComponent({
  emits: ['cancel', 'done'],
  props: {
    title: { type: String, default: 'Select Case' },
    isGroup: { type: Boolean, required: true },
  },
  components: {
    AppCaseViewPopUp,
    AppSkeletonCase,
    ItemCase,
    AppMessageShareSettings,
  },
  setup(props, { emit }) {
    const driveStore = useDriveStore();
    const mainStore = useMainStore();
    const messagesStore = useMessagesStore();
    const {
      scroller, pageIdx, isPageLoading, onScroll, setLastPage, onCacheScroll,
    } = UsePagination();

    const dataLoading = ref(false);
    const searchCases = ref<ICase[]>([]);

    const cases = computed(() => {
      if (driveStore.search) {
        return searchCases.value;
      }
      return driveStore.cases.data;
    });

    const previewCase = reactive({
      id: '', title: '', photosAmount: 0, filesAmount: 0, show: false,
    });

    const caseCheck = ref<any>({});
    const selectedList = computed(() => Object.keys(caseCheck.value).filter((item) => caseCheck.value[item]));

    const shareSettings: Ref<SHARE_SETTINGS[]> = ref([]);

    function doneAction() {
      if (!selectedList.value.length) {
        return;
      }
      selectedList.value.forEach((id) => {
        WebsocketService.sendCase(messagesStore.currentConversation!.info.id, id, '', props.isGroup, shareSettings.value);
      });
      emit('done');
    }

    async function fetchCacheData() {
      return driveStore.fetchCases();
    }

    async function fetchSearchData() {
      return DriveService.getCases(pageIdx.value, driveStore.search)
        .then((response) => {
          if (pageIdx.value === 0) {
            searchCases.value = response.results.data.items;
          } else {
            searchCases.value.push(...response.results.data.items);
          }
          return response;
        });
    }

    let timeout: ReturnType<typeof setTimeout>;
    watch(() => driveStore.search, (newSearch, oldSearch) => {
      clearTimeout(timeout);
      pageIdx.value = 0;
      dataLoading.value = !!newSearch;
      timeout = setTimeout(() => {
        fetchSearchData()
          .then((response) => {
            setLastPage(response.results.data.paging.items, response.results.data.paging.itemsOnPage);
          })
          .finally(() => {
            dataLoading.value = false;
          });
      }, 300);
    });

    function setPreviewCase($event, iCase: ICase) {
      if (driveStore.isGridCasesView) return;
      $event.stopPropagation();
      previewCase.id = iCase.id!;
      previewCase.title = iCase.title!;
      previewCase.filesAmount = iCase.filesAmount!;
      previewCase.photosAmount = iCase.photosAmount!;
      previewCase.show = true;
    }

    function selectCase(id: string) {
      caseCheck.value = { ...caseCheck.value, [id]: !caseCheck.value[id] };
      previewCase.show = false;
    }

    function handleScroll() {
      if (driveStore.search) {
        onScroll(fetchSearchData, DIRECTION.BOTTOM, dataLoading.value);
      } else {
        onCacheScroll(fetchCacheData, DIRECTION.BOTTOM, driveStore.cases.lastPage, driveStore.cases.page);
      }
    }

    function updateShareSettings(event) {
      shareSettings.value = [
        event.allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        event.allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        event.allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    }

    onMounted(() => {
      driveStore.search = '';
      shareSettings.value = [
        mainStore.sharingSettings[0].allowedResharing ? SHARE_SETTINGS.RESHARE_ALLOWED : SHARE_SETTINGS.RESHARE_DISABLED,
        mainStore.sharingSettings[0].allowedSave ? SHARE_SETTINGS.ALLOW_SAVE : SHARE_SETTINGS.DISABLE_SAVE,
        mainStore.sharingSettings[0].allowedDisplay ? SHARE_SETTINGS.DISPLAY_ATTRIBUTION : SHARE_SETTINGS.NOT_DISPLAY_ATTRIBUTION,
      ];
    });

    return {
      driveStore,
      isPageLoading,
      scroller,
      handleScroll,
      doneAction,
      PHOTO_VIEW_TYPE,
      previewCase,
      setPreviewCase,
      selectCase,
      updateShareSettings,
      selectedList,
      caseCheck,
      dataLoading,
      cases,
      shareSettings,
    };
  },
});
